// Canvas background picker — the Zoombar's color dot opens a dropdown
// (presets + native custom input) and writes the picked color to a
// document-level CSS var `--canvas-bg`. Every canvas Stage references it
// via `style: { background: 'var(--canvas-bg, <page default>)' }`
// (project canvas embed/Canvas.js, /studio studios.js root, /tickets
// board.js Stage, /team-and-agents Stage), so one pick recolors whichever
// canvas is active — the old path only poked the project canvas via
// `gs.canvasElement.setNodeStyles`. The CSS-var route is imperative on a
// stable node (documentElement), sidestepping v3.14's frozen reactive
// background factories on mounted stages. Clearing the var restores each
// page's own default. Persistence: the pre-existing per-theme cookie
// `canvas_${theme}_background`, restored in the dot's onInit and whenever
// the shell changes theme so light picks do not leak into dark mode.
import {
  clearCanvasBackgroundVar,
  resolveCanvasBackgroundTheme,
  setCanvasBackgroundVar,
  syncCanvasBackgroundForTheme,
  watchCanvasBackgroundTheme
} from '../functions/canvasBackground.js'
import { safeCall } from '../functions/safeCall.js'
import { touchFloor } from './_touchTarget.js'

export const CANVAS_BG_PRESETS_BY_THEME = {
  light: [
    { label: 'editor.canvasBg.paper', value: '#f2f3f5' },
    { label: 'editor.canvasBg.white', value: '#fafafb' },
    { label: 'editor.canvasBg.mist', value: '#e7eaf0' },
    { label: 'editor.canvasBg.coolGray', value: '#dfe3ea' },
    { label: 'editor.canvasBg.softBlue', value: '#eaf2ff' },
    { label: 'editor.canvasBg.sage', value: '#e9f4ee' },
    { label: 'editor.canvasBg.sand', value: '#f4efe6' },
    { label: 'editor.canvasBg.lavender', value: '#f0edfb' }
  ],
  dark: [
    { label: 'editor.canvasBg.graphite', value: '#161616' },
    { label: 'editor.canvasBg.nearBlack', value: '#0a0a0a' },
    { label: 'editor.canvasBg.darkGray', value: '#2e2e34' },
    { label: 'editor.canvasBg.slate', value: '#232a36' },
    { label: 'editor.canvasBg.deepBlue', value: '#10203a' },
    { label: 'editor.canvasBg.deepGreen', value: '#0f241a' },
    { label: 'editor.canvasBg.warmDark', value: '#262019' },
    { label: 'editor.canvasBg.lightBg', value: '#f2f3f5' }
  ]
}

export const canvasBackgroundPresetsForTheme = (theme) =>
  CANVAS_BG_PRESETS_BY_THEME[resolveCanvasBackgroundTheme(theme)]

export const Zoombar = {
  // FA204 / frank trap — DOT_DEFAULTS used to be a bare module-scope const
  // read from inside `onInit`/`onClick` (serialized factories); it silently
  // read as undefined once this component is frank.toJSON'd for platform
  // delivery (workspace d6ea1142, project_frank_drops_module_local_helpers).
  // Moved into `scope`, called via `el.scope.DOT_DEFAULTS`.
  // CANVAS_BG_PRESETS_BY_THEME/canvasBackgroundPresetsForTheme above stay
  // module-scope EXPORTS (canvas/__tests__/theme.test.js and
  // workspace-ui/functions/workspaceUiManifest.js import them directly as
  // plain JS) — canvasBackgroundPresetsForTheme is duplicated below as a
  // scope method (referencing the same exported table at build time, so no
  // data is retyped) so the in-file `onClick` call site also resolves
  // post-frank.
  scope: {
    DOT_DEFAULTS: {
      dark: '#A3ADB6',
      light: '#bac1c9'
    },
    CANVAS_BG_PRESETS_BY_THEME,
    canvasBackgroundPresetsForTheme(theme) {
      return this.CANVAS_BG_PRESETS_BY_THEME[resolveCanvasBackgroundTheme(theme)]
    }
  },
  // if: (el, s) => el.getUserSettings('allowZoombar'),
  extends: 'IconNavbar',
  // Touch declutter: on a coarse pointer (iPad) the bar loosens up so the
  // remaining controls (background · settings · docs) get comfortable spacing
  // once the mouse-only zoom stepper + keyboard-shortcuts button drop out.
  '@coarse': { gap: 'Y1' },
  ZoomInNavbar: {
    // Mouse-only: the -/100%/+ stepper (and its % readout) is redundant with
    // pinch-zoom on touch, so hide it under a coarse pointer to declutter.
    '@coarse': { display: 'none' }
  },
  Box: {
    tag: 'button',
    type: 'button',
    title: (el) => el.call('polyglot', 'editor.canvasBg.title'),
    'aria-label': (el) => el.call('polyglot', 'editor.canvasBg.title'),
    width: 'A1',
    height: 'A1',
    aspectRatio: '1 / 1',
    round: '100%',
    overflow: 'hidden',
    flexShrink: '0',
    cursor: 'pointer',
    border: '0',
    padding: '0',
    background: '#A3ADB6',
    // A finger's target: the swatch is A1 (18-19px measured on dev DFI with a
    // touch device simulated, phones and iPads) — the 44pt floor
    // (_touchTarget.js). It clips nothing of its own (its fill is its own
    // round), so it stops clipping the hit area under a coarse pointer.
    '@coarse': { position: 'relative', overflow: 'visible', ...touchFloor },
    onInit: (el, s) => {
      const doc = el.context?.document || document
      const globalTheme = resolveCanvasBackgroundTheme(safeCall(el, 'getGlobalTheme'), doc)
      const colorFromCookie = syncCanvasBackgroundForTheme(globalTheme, doc, (name) =>
        safeCall(el, 'getCookie', name)
      )
      const initValue = colorFromCookie || el.scope.DOT_DEFAULTS[globalTheme || 'dark']
      el.setNodeStyles({ background: initValue })
      el.value = initValue
      // Singleton document-level watcher: re-syncs the per-theme cookie
      // var on every data-theme flip (user OR OS-driven 'auto'), so a
      // light pick never leaks into dark mode. Zoombar mounts once at
      // the shell root, so this runs once per document.
      watchCanvasBackgroundTheme(doc)
    },
    onClick: (ev, el, s) => {
      ev.stopPropagation()
      const box = el
      const doc = el.context?.document || document
      const globalTheme = resolveCanvasBackgroundTheme(safeCall(el, 'getGlobalTheme'), doc)
      const cookieKey = `canvas_${globalTheme}_background`
      const presets = el.scope.canvasBackgroundPresetsForTheme(globalTheme)

      const apply = (value) => {
        box.setNodeStyles({ background: value })
        box.value = value
        setCanvasBackgroundVar(doc, value)
        safeCall(box, 'setCookie', cookieKey, value)
      }
      const reset = () => {
        const fallback = el.scope.DOT_DEFAULTS[globalTheme || 'dark']
        box.setNodeStyles({ background: fallback })
        box.value = fallback
        clearCanvasBackgroundVar(doc)
        safeCall(box, 'removeCookie', cookieKey)
      }

      // Dropdown portal content — built here in the opener so `box` /
      // `apply` are captured in scope (the portal doesn't inherit page
      // state) and rows carry explicit `tag`s (childExtends tag doesn't
      // apply inside portal content).
      const content = {
        flow: 'y',
        gap: 'Z',
        padding: 'Z1',

        PickerTitle: {
          tag: 'span',
          text: '{{ editor.canvasBg.title | polyglot }}',
          fontSize: 'Z',
          fontWeight: '700',
          color: 'caption',
          letterSpacing: '.04em',
          textTransform: 'uppercase'
        },

        PickerSwatches: {
          display: 'grid',
          gridTemplateColumns: 'repeat(4, auto)',
          gap: 'Y',
          ...presets.reduce((acc, preset, i) => {
            acc['PickerSwatch_' + i] = {
              tag: 'button',
              type: 'button',
              title: (el) => el.call('polyglot', preset.label),
              'aria-label': (el) => el.call('polyglot', preset.label),
              width: 'A1',
              aspectRatio: '1 / 1',
              round: '100%',
              padding: '0',
              cursor: 'pointer',
              flexShrink: '0',
              background: preset.value,
              border: 'hairline solid',
              borderColor: 'line',
              onClick: (e, it) => {
                e.stopPropagation()
                apply(preset.value)
                it.call('closeDropdown', 'canvasBgPicker')
              }
            }
            return acc
          }, {})
        },

        PickerCustomRow: {
          flow: 'x',
          align: 'center space-between',
          gap: 'Z',
          PickerCustomLabel: {
            tag: 'span',
            text: '{{ editor.canvasBg.custom | polyglot }}',
            fontSize: 'Z1',
            color: 'paragraph'
          },
          PickerCustomInput: {
            tag: 'input',
            type: 'color',
            // The visible "Custom" label beside it is a sibling span, not a
            // <label for>, so the control itself carried no accessible name.
            'aria-label': (it) => it.call('polyglot', 'editor.canvasBg.custom'),
            width: 'B',
            height: 'A1',
            padding: '0',
            border: '0',
            round: 'Y',
            cursor: 'pointer',
            background: 'transparent',
            // Content is rebuilt on every open, so the current pick is
            // known at build time — set it as the input's value prop.
            value: /^#[0-9a-fA-F]{6}$/.test(box.value || '') ? box.value : '#161616',
            // Applies live while the user drags the native picker.
            onInput: (e, it) => apply(it.node.value)
          }
        },

        PickerResetRow: {
          tag: 'button',
          type: 'button',
          text: '{{ editor.canvasBg.reset | polyglot }}',
          fontSize: 'Z1',
          color: 'paragraph',
          textAlign: 'left',
          padding: 'Y Z',
          round: 'radiusControl',
          border: '0',
          cursor: 'pointer',
          background: 'transparent',
          ':hover': { background: 'hover', color: 'title' },
          onClick: (e, it) => {
            e.stopPropagation()
            reset()
            it.call('closeDropdown', 'canvasBgPicker')
          }
        }
      }

      el.call('openDropdown', {
        id: 'canvasBgPicker',
        anchorEl: el,
        placement: 'top-end',
        // Explicit shell paint — this picker's rows render directly on the
        // dropdown shell (no own card). The plugin Panel is chromeless by
        // default since FW-DROPDOWN-PANEL-STALE-SHELL-PAINT-1; opt back in.
        theme: 'canvas-card-glass',
        shadow: 'overlay',
        content
      })
    }
  },
  NavbarButtonSet: {
    padding: '0',
    Settings: {
      icon: 'sf settings',
      onClick: (ev, el) => {
        el.call('openRouterModal', '/settings', {
          key: '/settings'
        })
      },
      title: (el) => el.call('polyglot', 'editor.zoombar.settings')
    },
    Keyboard: {
      title: (el) => el.call('polyglot', 'editor.zoombar.shortcutsTooltip'),
      'aria-label': (el) => el.call('polyglot', 'editor.zoombar.shortcuts'),
      icon: 'sf keyboard',
      // Keyboard shortcuts need a keyboard — don't render on a coarse pointer
      // (iPad). `if` excludes at mount, so desktop (fine) is completely
      // untouched and we avoid fighting NavbarButton's inline display with a
      // @coarse display:none (which loses to it).
      if: (el) => !el.call('isCoarsePointer'),
      onClick: (ev, el) => el.call('openKeyboardShortcuts')
    },
    Question: {
      title: (el) => el.call('polyglot', 'editor.zoombar.docs'),
      icon: 'sf question',
      onClick: (ev, el, s) => {
        // canvas's v1→v2 bridge (Rule 63 fix — this used to be the SAME name
        // as the real shared setWindow primitive and shadowed it shell-wide;
        // see canvas/methods/index.js). In v2 this opens the shortcuts
        // overlay instead of the (nonexistent in v2) onboarding explorer
        // window.
        el.call('bridgeSetWindow', 'explorer', 'onboarding')
      }
    }
    // Chat: {
    //   title: 'Support',
    //   icon: 'support',
    //   onClick: (ev, el, s) => {
    //     el.call('loadIntercom')
    //   }
    // }
  }
  // NavbarTooltip removed (2026-06-08 per Nika) — the buttons' `title`
  // props now surface via NavbarButton's TooltipTrigger → the shared
  // @symbo.ls/tooltip TooltipRoot, so the legacy per-bar tooltip is gone.
}
